<template>
  <TransitionRoot
    :show="show"
    as="template"
    leave="duration-200"
  >
    <Dialog
      class="relative z-50"
      @close="close"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300 motion-reduce:transition-none"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200 motion-reduce:transition-none"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
      </TransitionChild>
      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300 motion-reduce:hover:transform-none"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95 "
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200 motion-reduce:hover:transform-none"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              :as="as"
              :class="maxWidthClass"
              class="flex flex-col gap-y-6 rounded-2xl bg-white p-6"
              novalidate
              @submit.prevent="$emit('submitted')"
            >
              <div class="flex items-center justify-between gap-x-4">
                <DialogTitle
                  class="wc-overline"
                  v-text="title"
                />

                <button
                  v-if="closeable"
                  class="wc-focus cursor-pointer"
                  type="button"
                  @click="close"
                >
                  <span
                    class="sr-only"
                    v-text="`Close ${title} Modal`"
                  />
                  <IconX
                    :size="24"
                    aria-hidden="true"
                  />
                </button>
              </div>

              <slot />

              <div class="flex flex-wrap items-center justify-end gap-4">
                <slot name="footer" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue'
import { computed } from 'vue'
import { IconX } from '@tabler/icons-vue'

defineOptions({
  name: 'BaseModal',
})

const props = withDefaults(defineProps<{
  show: boolean
  title: string
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  closeable?: boolean
  as?: 'form' | 'div'
}>(), {
  maxWidth: '2xl',
  closeable: true,
  as: 'div',
})

const emit = defineEmits<{
  close: [void],
  submitted: [void]
}>()

const close = () => {
  if (props.closeable) {
    emit('close')
  }
}

const maxWidthClass = computed(() => {
  return {
    'sm': 'sm:max-w-sm',
    'md': 'sm:max-w-md',
    'lg': 'sm:max-w-lg',
    'xl': 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
  }[props.maxWidth]
})
</script>
